import React, { useState, useRef } from 'react';
import { Document, Page, Text, View, StyleSheet, PDFDownloadLink, Image } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';

// PDF Styles (keep as is for PDF generation)
const styles = StyleSheet.create({
  page: {
    padding: 50,
    backgroundColor: '#ffffff',
    fontFamily: 'Helvetica',
  },
  header: {
    marginBottom: 30,
    textAlign: 'center',
    borderBottom: '1px solid #e5e5e5',
    paddingBottom: 20,
  },
  logo: {
    width: 80,
    height: 80,
    marginBottom: 10,
  },
  companyName: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: 5,
  },
  companyAddress: {
    fontSize: 10,
    color: '#666',
    marginTop: 5,
  },
  title: {
    fontSize: 20,
    fontWeight: 'bold',
    textAlign: 'center',
    marginVertical: 20,
    textDecoration: 'underline',
  },
  date: {
    fontSize: 12,
    textAlign: 'right',
    marginBottom: 20,
  },
  subject: {
    fontSize: 12,
    fontWeight: 'bold',
    marginVertical: 10,
  },
  salutation: {
    fontSize: 12,
    marginBottom: 10,
  },
  content: {
    fontSize: 11,
    lineHeight: 1.5,
    marginBottom: 10,
    textAlign: 'justify',
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    marginTop: 15,
    marginBottom: 8,
    backgroundColor: '#f3f4f6',
    padding: 5,
  },
  table: {
    marginVertical: 10,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e5e5',
    paddingVertical: 5,
  },
  tableLabel: {
    width: '30%',
    fontSize: 10,
    fontWeight: 'bold',
  },
  tableValue: {
    width: '70%',
    fontSize: 10,
  },
  signature: {
    marginTop: 40,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureLine: {
    width: 200,
    borderTopWidth: 1,
    borderTopColor: '#000',
    marginTop: 30,
    paddingTop: 5,
  },
  signatureText: {
    fontSize: 10,
    textAlign: 'center',
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 50,
    right: 50,
    textAlign: 'center',
    fontSize: 8,
    color: '#999',
    borderTopWidth: 1,
    borderTopColor: '#e5e5e5',
    paddingTop: 10,
  },
});

// PDF Document Component (keep as is)
const OfferLetterPDF = ({ formData, companyLogo, signature }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      <View style={styles.header}>
        {companyLogo && <Image src={companyLogo} style={styles.logo} />}
        <Text style={styles.companyName}>AIZOPS PVT LTD</Text>
        <Text style={styles.companyAddress}>
          City Centre, 4th Floor, Matigara, Siliguri - 734010, West Bengal, India
        </Text>
        <Text style={styles.companyAddress}>Phone: +91 33 1234 5678 | Email: careers@aizops.com</Text>
      </View>

      <Text style={styles.title}>OFFER LETTER</Text>
      <Text style={styles.date}>Date: {new Date().toLocaleDateString('en-IN')}</Text>
      <Text style={styles.subject}>Subject: Offer of Employment</Text>
      <Text style={styles.salutation}>Dear {formData.candidateName},</Text>

      <Text style={styles.content}>
        Following our recent discussions, we are pleased to offer you the position of <Text style={{ fontWeight: 'bold' }}>{formData.position}</Text> at AIZops Technologies. We believe your skills and experience will be a valuable asset to our growing team.
      </Text>

      <View style={styles.sectionTitle}>
        <Text>1. OFFER DETAILS</Text>
      </View>
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Position:</Text>
          <Text style={styles.tableValue}>{formData.position}</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Role Type:</Text>
          <Text style={styles.tableValue}>{formData.role}</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Department:</Text>
          <Text style={styles.tableValue}>{formData.department}</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Reporting To:</Text>
          <Text style={styles.tableValue}>{formData.reportingTo}</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Joining Date:</Text>
          <Text style={styles.tableValue}>{formData.joiningDate}</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Work Location:</Text>
          <Text style={styles.tableValue}>City Centre, Siliguri</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Work Mode:</Text>
          <Text style={styles.tableValue}>{formData.workMode}</Text>
        </View>
      </View>

      <View style={styles.sectionTitle}>
        <Text>2. COMPENSATION & BENEFITS</Text>
      </View>
      <View style={styles.table}>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Annual CTC:</Text>
          <Text style={styles.tableValue}>₹{formData.ctc.toLocaleString()} per annum</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Monthly Salary:</Text>
          <Text style={styles.tableValue}>₹{Math.round(formData.ctc / 12).toLocaleString()}</Text>
        </View>
        <View style={styles.tableRow}>
          <Text style={styles.tableLabel}>Probation Period:</Text>
          <Text style={styles.tableValue}>{formData.probationPeriod} months</Text>
        </View>
      </View>

      <View style={styles.sectionTitle}>
        <Text>3. TERMS & CONDITIONS</Text>
      </View>
      <Text style={styles.content}>
        • This offer is subject to successful background verification.{'\n'}
        • You will be governed by the company's policies and code of conduct.{'\n'}
        • Confidentiality agreement must be signed before joining.{'\n'}
        • Notice period: {formData.noticePeriod} days during probation, 60 days post-confirmation.
      </Text>

      <View style={styles.sectionTitle}>
        <Text>4. ACCEPTANCE</Text>
      </View>
      <Text style={styles.content}>
        Please confirm your acceptance of this offer by signing below and returning a copy within {formData.acceptanceDays} days. We look forward to welcoming you to the AIZops family!
      </Text>

      <View style={styles.signature}>
        <View>
          <Text style={styles.signatureText}>For AIZops Technologies</Text>
          <View style={styles.signatureLine}>
            {signature && <Image src={signature} style={{ width: 100, height: 40 }} />}
            <Text style={styles.signatureText}>Authorized Signatory</Text>
            <Text style={styles.signatureText}>CEO, AIZops Technologies</Text>
          </View>
        </View>
        <View>
          <Text style={styles.signatureText}>Accepted by Candidate</Text>
          <View style={styles.signatureLine}>
            <Text style={styles.signatureText}>___________________</Text>
            <Text style={styles.signatureText}>{formData.candidateName}</Text>
            <Text style={styles.signatureText}>Date: {new Date().toLocaleDateString('en-IN')}</Text>
          </View>
        </View>
      </View>

      <Text style={styles.footer}>
        AIZops Technologies | City Centre, Siliguri | www.aizops.com | This is a system-generated document
      </Text>
    </Page>
  </Document>
);

const OfferLetterGenerator = () => {
  const [formData, setFormData] = useState({
    candidateName: '',
    position: '',
    role: 'employee',
    department: '',
    reportingTo: '',
    joiningDate: '',
    ctc: '',
    workMode: 'Hybrid',
    probationPeriod: 3,
    noticePeriod: 30,
    acceptanceDays: 7,
    email: '',
    phone: '',
  });

  const [errors, setErrors] = useState({
    candidateName: '',
    email: '',
    phone: '',
    joiningDate: '',
    ctc: '',
    probationPeriod: '',
    noticePeriod: '',
    acceptanceDays: ''
  });

  const [companyLogo, setCompanyLogo] = useState(null);
  const [signature, setSignature] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState(null);
  const [generated, setGenerated] = useState(false);
  const [downloadFormat, setDownloadFormat] = useState('pdf');

  const roles = [
    { value: 'intern', label: 'Intern' },
    { value: 'employee', label: 'Software Developer' },
    { value: 'manager', label: 'Manager' },
    { value: 'hr', label: 'HR Executive' },
    { value: 'senior', label: 'Senior Developer' },
    { value: 'lead', label: 'Team Lead' },
  ];

  const departments = [
    'Software Development', 'IT Infrastructure', 'Human Resources', 'Marketing',
    'Sales', 'Finance', 'Quality Assurance', 'DevOps', 'Product Management',
  ];

  const reportingManagers = [
    'Aman Verma (IT Manager)', 'Sanjiv Singh (HR Head)', 'Harsh Sharma (Senior Developer)',
    'Dr. Apj Abdul Kalam (R&D Head)', 'CEO Office',
  ];

  // Validate email format
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Validate phone number (exactly 10 digits)
  const isValidPhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  // Validate candidate name (max 100 chars, not empty)
  const isValidCandidateName = (name) => {
    return name && name.trim().length > 0 && name.length <= 100;
  };

  // Validate joining date (between 2025 and 2099)
  const isValidJoiningDate = (date) => {
    if (!date) return false;
    const year = new Date(date).getFullYear();
    return year >= 2025 && year <= 2099;
  };

  // Validate positive number (greater than 0)
  const isPositiveNumber = (value) => {
    const num = parseFloat(value);
    return !isNaN(num) && num > 0;
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        setCompanyLogo(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload PNG or JPG file');
    }
  };

  const handleSignatureUpload = (e) => {
    const file = e.target.files[0];
    if (file && (file.type === 'image/png' || file.type === 'image/jpeg')) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSignaturePreview(reader.result);
        setSignature(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert('Please upload PNG or JPG file');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear error for the field being edited
    if (name === 'candidateName' || name === 'email' || name === 'phone' || name === 'joiningDate' ||
        name === 'ctc' || name === 'probationPeriod' || name === 'noticePeriod' || name === 'acceptanceDays') {
      setErrors({ ...errors, [name]: '' });
    }
    
    // Limit candidate name to 100 characters
    if (name === 'candidateName' && value.length > 100) {
      setErrors({ ...errors, candidateName: 'Candidate name cannot exceed 100 characters' });
      return;
    }
    
    // Limit email to 100 characters
    if (name === 'email' && value.length > 100) {
      setErrors({ ...errors, email: 'Email address cannot exceed 100 characters' });
      return;
    }
    
    // Handle phone number - only allow digits and limit to 10 characters
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      if (digitsOnly.length <= 10) {
        setFormData({ ...formData, [name]: digitsOnly });
        if (digitsOnly.length === 10 && !isValidPhoneNumber(digitsOnly)) {
          setErrors({ ...errors, phone: 'Please enter a valid 10-digit phone number' });
        } else {
          setErrors({ ...errors, phone: '' });
        }
      }
      return;
    }
    
    // Handle numeric fields - ensure positive numbers
    if (name === 'ctc' || name === 'probationPeriod' || name === 'noticePeriod' || name === 'acceptanceDays') {
      if (value === '') {
        setFormData({ ...formData, [name]: '' });
        return;
      }
      
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        if (numValue <= 0) {
          let errorMessage = '';
          if (name === 'ctc') errorMessage = 'Annual CTC must be greater than 0';
          if (name === 'probationPeriod') errorMessage = 'Probation period must be greater than 0 months';
          if (name === 'noticePeriod') errorMessage = 'Notice period must be greater than 0 days';
          if (name === 'acceptanceDays') errorMessage = 'Acceptance deadline must be greater than 0 days';
          setErrors({ ...errors, [name]: errorMessage });
        } else {
          setErrors({ ...errors, [name]: '' });
        }
      }
      setFormData({ ...formData, [name]: value });
      return;
    }
    
    setFormData({ ...formData, [name]: value });
  };

  const handleJoiningDateChange = (e) => {
    const date = e.target.value;
    setFormData({ ...formData, joiningDate: date });
    
    if (date && !isValidJoiningDate(date)) {
      setErrors({ ...errors, joiningDate: 'Joining date must be between 2025 and 2099' });
    } else {
      setErrors({ ...errors, joiningDate: '' });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    let hasErrors = false;
    const newErrors = { 
      candidateName: '', 
      email: '', 
      phone: '', 
      joiningDate: '',
      ctc: '',
      probationPeriod: '',
      noticePeriod: '',
      acceptanceDays: ''
    };
    
    // Validate candidate name
    if (!formData.candidateName || formData.candidateName.trim().length === 0) {
      newErrors.candidateName = 'Candidate name is required';
      hasErrors = true;
    } else if (formData.candidateName.length > 100) {
      newErrors.candidateName = 'Candidate name cannot exceed 100 characters';
      hasErrors = true;
    }
    
    // Validate email (only if provided)
    if (formData.email && formData.email.trim().length > 0) {
      if (formData.email.length > 100) {
        newErrors.email = 'Email address cannot exceed 100 characters';
        hasErrors = true;
      } else if (!isValidEmail(formData.email)) {
        newErrors.email = 'Please enter a valid email address (e.g., name@example.com)';
        hasErrors = true;
      }
    }
    
    // Validate phone number (only if provided)
    if (formData.phone && formData.phone.trim().length > 0) {
      if (!isValidPhoneNumber(formData.phone)) {
        newErrors.phone = 'Phone number must be exactly 10 digits';
        hasErrors = true;
      }
    }
    
    // Validate joining date
    if (!formData.joiningDate) {
      newErrors.joiningDate = 'Joining date is required';
      hasErrors = true;
    } else if (!isValidJoiningDate(formData.joiningDate)) {
      newErrors.joiningDate = 'Joining date must be between 2025 and 2099';
      hasErrors = true;
    }
    
    // Validate CTC (must be positive)
    if (!formData.ctc) {
      newErrors.ctc = 'Annual CTC is required';
      hasErrors = true;
    } else if (!isPositiveNumber(formData.ctc)) {
      newErrors.ctc = 'Annual CTC must be greater than 0';
      hasErrors = true;
    }
    
    // Validate Probation Period (must be positive)
    if (!formData.probationPeriod) {
      newErrors.probationPeriod = 'Probation period is required';
      hasErrors = true;
    } else if (!isPositiveNumber(formData.probationPeriod)) {
      newErrors.probationPeriod = 'Probation period must be greater than 0 months';
      hasErrors = true;
    }
    
    // Validate Notice Period (must be positive)
    if (!formData.noticePeriod) {
      newErrors.noticePeriod = 'Notice period is required';
      hasErrors = true;
    } else if (!isPositiveNumber(formData.noticePeriod)) {
      newErrors.noticePeriod = 'Notice period must be greater than 0 days';
      hasErrors = true;
    }
    
    // Validate Acceptance Days (must be positive)
    if (!formData.acceptanceDays) {
      newErrors.acceptanceDays = 'Acceptance deadline is required';
      hasErrors = true;
    } else if (!isPositiveNumber(formData.acceptanceDays)) {
      newErrors.acceptanceDays = 'Acceptance deadline must be greater than 0 days';
      hasErrors = true;
    }
    
    // Validate other required fields
    if (!formData.position) {
      alert('Please select a position');
      return;
    }
    
    if (hasErrors) {
      setErrors(newErrors);
      return;
    }
    
    setGenerated(true);
  };

  // Generate Word Document
  const generateWordDocument = () => {
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Offer Letter - ${formData.candidateName}</title>
        <style>
          body {
            font-family: 'Times New Roman', serif;
            margin: 50px;
            line-height: 1.6;
            color: #1a1a1a;
          }
          .container {
            max-width: 800px;
            margin: 0 auto;
            background: white;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #1e3a8a;
            padding-bottom: 20px;
          }
          .logo {
            max-width: 100px;
            margin-bottom: 10px;
          }
          .company-name {
            font-size: 28px;
            font-weight: bold;
            color: #1e3a8a;
            letter-spacing: 1px;
            margin-bottom: 5px;
          }
          .company-tagline {
            font-size: 11px;
            color: #666;
            font-style: italic;
          }
          .company-address {
            font-size: 10px;
            color: #555;
            margin-top: 5px;
          }
          .title {
            font-size: 22px;
            font-weight: bold;
            text-align: center;
            margin: 30px 0;
            text-transform: uppercase;
            letter-spacing: 2px;
            color: #1e3a8a;
          }
          .ref-number {
            text-align: right;
            font-size: 10px;
            color: #666;
            margin-bottom: 10px;
          }
          .date {
            text-align: right;
            margin-bottom: 25px;
            font-size: 12px;
          }
          .subject {
            font-size: 13px;
            font-weight: bold;
            margin: 20px 0 10px;
            color: #1e3a8a;
          }
          .salutation {
            font-size: 12px;
            margin-bottom: 15px;
          }
          .content {
            font-size: 11px;
            margin-bottom: 15px;
            text-align: justify;
          }
          .section-title {
            font-size: 13px;
            font-weight: bold;
            margin: 20px 0 10px;
            padding: 8px;
            background-color: #f0f4f8;
            border-left: 3px solid #1e3a8a;
            color: #1e3a8a;
          }
          .offer-table {
            width: 100%;
            margin: 15px 0;
            border-collapse: collapse;
          }
          .offer-table td {
            padding: 8px;
            border-bottom: 1px solid #e5e7eb;
          }
          .label {
            width: 30%;
            font-weight: bold;
            background-color: #f9fafb;
          }
          .signature-section {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
          }
          .signature-box {
            width: 45%;
            text-align: center;
          }
          .signature-line {
            margin-top: 40px;
            padding-top: 5px;
            border-top: 1px solid #000;
          }
          .footer {
            text-align: center;
            font-size: 8px;
            color: #999;
            margin-top: 60px;
            padding-top: 10px;
            border-top: 1px solid #e5e5e5;
          }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            ${companyLogo ? `<img src="${companyLogo}" class="logo" />` : ''}
            <div class="company-name">AIZops Technologies</div>
            <div class="company-tagline">Innovating Tomorrow's Enterprise Solutions</div>
            <div class="company-address">City Centre, 4th Floor, Matigara, Siliguri - 734010, West Bengal, India</div>
            <div class="company-address">📞 +91 33 1234 5678 | ✉️ careers@aizops.com | 🌐 www.aizops.com</div>
          </div>

          <div class="ref-number">
            Ref: AIZ/OL/${new Date().getFullYear()}/${Math.floor(Math.random() * 1000)}
          </div>

          <div class="title">Offer of Employment</div>
          
          <div class="date">
            Date: ${new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
          </div>

          <div class="subject">Subject: Offer Letter for the position of ${formData.position}</div>
          
          <div class="salutation">
            Dear ${formData.candidateName},
          </div>

          <div class="content">
            We are delighted to extend this offer of employment to you for the position of <strong>${formData.position}</strong> at <strong>AIZops Technologies</strong>. After a thorough review of your qualifications and experience, we believe you will make a valuable contribution to our organization's growth and success.
          </div>

          <div class="section-title">1. Employment Terms</div>
          <table class="offer-table">
            <tr><td class="label">Position:</td><td>${formData.position}</td></tr>
            <tr><td class="label">Role Category:</td><td>${formData.role}</td></tr>
            <tr><td class="label">Department:</td><td>${formData.department}</td></tr>
            <tr><td class="label">Reporting Manager:</td><td>${formData.reportingTo}</td></tr>
            <tr><td class="label">Date of Joining:</td><td>${formData.joiningDate}</td></tr>
            <tr><td class="label">Work Location:</td><td>City Centre, Siliguri</td></tr>
            <tr><td class="label">Work Arrangement:</td><td>${formData.workMode}</td></tr>
          </table>

          <div class="section-title">2. Compensation Structure</div>
          <table class="offer-table">
            <tr><td class="label">Annual CTC:</td><td>₹${parseInt(formData.ctc).toLocaleString('en-IN')} per annum</td></tr>
            <tr><td class="label">Monthly Gross:</td><td>₹${Math.round(parseInt(formData.ctc) / 12).toLocaleString('en-IN')}</td></tr>
            <tr><td class="label">Probation Period:</td><td>${formData.probationPeriod} months</td></tr>
            <tr><td class="label">Notice Period:</td><td>${formData.noticePeriod} days (during probation) / 60 days (post-confirmation)</td></tr>
          </table>

          <div class="section-title">3. Benefits & Perks</div>
          <div class="content">
            • Comprehensive Health Insurance coverage for self and family<br/>
            • Performance-based annual bonus up to 20% of CTC<br/>
            • 24 paid leaves annually + 12 public holidays<br/>
            • Professional development allowance of ₹20,000 per annum<br/>
            • Provident Fund as per government regulations<br/>
            • Gratuity as per Payment of Gratuity Act
          </div>

          <div class="section-title">4. Terms of Employment</div>
          <div class="content">
            This offer is subject to the successful completion of background verification and reference checks. Your employment will be governed by the company's policies, procedures, and code of conduct, which will be shared during your onboarding.
          </div>

          <div class="section-title">5. Confidentiality & IP Rights</div>
          <div class="content">
            You will be required to sign a confidentiality and non-disclosure agreement prior to joining. All intellectual property created during your employment shall be the sole property of AIZops Technologies.
          </div>

          <div class="section-title">6. Acceptance</div>
          <div class="content">
            Please indicate your acceptance of this offer by signing below and returning this letter within ${formData.acceptanceDays} calendar days from the date of this letter. This offer will remain valid until ${new Date(Date.now() + formData.acceptanceDays * 86400000).toLocaleDateString('en-IN')}.
          </div>

          <div class="signature-section">
            <div class="signature-box">
              <div class="signature-line">
                ${signature ? `<img src="${signature}" style="max-width: 120px; height: auto;" />` : ''}
                <div style="margin-top: 10px;"><strong>For AIZops Technologies</strong></div>
                <div>Authorized Signatory</div>
                <div>CEO, AIZops Technologies</div>
              </div>
            </div>
            <div class="signature-box">
              <div class="signature-line">
                <div style="margin-bottom: 30px;">___________________</div>
                <div><strong>Accepted by</strong></div>
                <div>${formData.candidateName}</div>
                <div>Date: ${new Date().toLocaleDateString('en-IN')}</div>
              </div>
            </div>
          </div>

          <div class="footer">
            <strong>AIZops Technologies Pvt. Ltd.</strong> | CIN: U72900WB2024PTC123456 | GST: 19AABCA1234A1Z<br/>
            This is a computer-generated document and does not require a physical signature.
          </div>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'application/msword' });
    saveAs(blob, `Offer_Letter_${formData.candidateName.replace(/\s/g, '_')}.doc`);
    alert('Word document downloaded successfully!');
  };

  const sendEmail = () => {
    if (formData.email && !isValidEmail(formData.email)) {
      alert('Please enter a valid email address before sending.');
      return;
    }
    alert(`Offer letter would be sent to ${formData.email || 'candidate@email.com'}\n\nIn production, this would send an actual email with the PDF/Word attachment.`);
  };

  if (generated) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        {/* Success Banner - Classic MNC Style */}
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-600 rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-800">Offer Letter Generated Successfully</h2>
                <p className="text-sm text-gray-600">Your professional offer letter is ready for {formData.candidateName}</p>
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Reference: AIZ/OL/{new Date().getFullYear()}/{Math.floor(Math.random() * 1000)}
            </div>
          </div>
        </div>

        {/* Download Options */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b border-gray-200">Download Options</h3>
          
          <div className="flex flex-wrap gap-6 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="pdf"
                checked={downloadFormat === 'pdf'}
                onChange={(e) => setDownloadFormat(e.target.value)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">PDF Document (.pdf)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="word"
                checked={downloadFormat === 'word'}
                onChange={(e) => setDownloadFormat(e.target.value)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-gray-700">Word Document (.doc)</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-4">
            {downloadFormat === 'pdf' ? (
              <PDFDownloadLink
                document={<OfferLetterPDF formData={formData} companyLogo={companyLogo} signature={signature} />}
                fileName={`Offer_Letter_${formData.candidateName.replace(/\s/g, '_')}.pdf`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm font-medium"
              >
                {({ loading }) => loading ? 'Generating PDF...' : '📄 Download PDF Document'}
              </PDFDownloadLink>
            ) : (
              <button
                onClick={generateWordDocument}
                className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition shadow-sm font-medium"
              >
                📝 Download Word Document
              </button>
            )}

            <button
              onClick={sendEmail}
              className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition shadow-sm font-medium"
            >
              📧 Send via Email
            </button>

            <button
              onClick={() => setGenerated(false)}
              className="inline-flex items-center gap-2 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
            >
              ✏️ Create Another Offer
            </button>
          </div>
        </div>

        {/* Preview Information */}
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-md font-semibold text-gray-700 mb-3">Quick Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Candidate</p>
              <p className="font-medium text-gray-800">{formData.candidateName}</p>
            </div>
            <div>
              <p className="text-gray-500">Position</p>
              <p className="font-medium text-gray-800">{formData.position}</p>
            </div>
            <div>
              <p className="text-gray-500">CTC</p>
              <p className="font-medium text-gray-800">₹{parseInt(formData.ctc).toLocaleString()}</p>
            </div>
            <div>
              <p className="text-gray-500">Joining Date</p>
              <p className="font-medium text-gray-800">{formData.joiningDate}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-white-50 to-blue-600 px-8 py-6">
          <h1 className="text-2xl font-bold text-white mb-1">Offer Letter Generator</h1>
          <p className="text-blue-100 text-sm">Create professional employment offer letters in MNC standard format</p>
        </div>

        <form onSubmit={handleSubmit} className="p-8">
          {/* Company Branding Section */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Company Branding
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleLogoUpload}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {logoPreview && (
                  <div className="mt-3">
                    <img src={logoPreview} alt="Logo Preview" className="h-16 w-auto object-contain border rounded p-1" />
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Authorized Signature</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg"
                  onChange={handleSignatureUpload}
                  className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {signaturePreview && (
                  <div className="mt-3">
                    <img src={signaturePreview} alt="Signature Preview" className="h-12 w-auto object-contain border rounded p-1" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Candidate Information */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Candidate Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Candidate Name <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Max 100 characters)</span>
                </label>
                <input 
                  type="text" 
                  name="candidateName" 
                  required 
                  maxLength="100"
                  className={`w-full px-3 py-2 border ${errors.candidateName ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.candidateName} 
                  onChange={handleChange} 
                  placeholder="Enter full name (max 100 characters)" 
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.candidateName && (
                    <p className="text-red-500 text-xs">{errors.candidateName}</p>
                  )}
                  <p className={`text-xs ${formData.candidateName.length > 90 ? 'text-orange-500' : 'text-gray-400'} ml-auto`}>
                    {formData.candidateName.length}/100
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                  <span className="text-xs text-gray-500 ml-2">(Optional, max 100 chars)</span>
                </label>
                <input 
                  type="email" 
                  name="email" 
                  maxLength="100"
                  className={`w-full px-3 py-2 border ${errors.email ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.email} 
                  onChange={handleChange} 
                  placeholder="candidate@example.com (max 100 characters)" 
                />
                <div className="flex justify-between items-center mt-1">
                  {errors.email && (
                    <p className="text-red-500 text-xs">{errors.email}</p>
                  )}
                  <p className={`text-xs ${formData.email.length > 90 ? 'text-orange-500' : 'text-gray-400'} ml-auto`}>
                    {formData.email.length}/100
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number
                  <span className="text-xs text-gray-500 ml-2">(Optional, exactly 10 digits)</span>
                </label>
                <input 
                  type="tel" 
                  name="phone" 
                  className={`w-full px-3 py-2 border ${errors.phone ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.phone} 
                  onChange={handleChange} 
                  placeholder="Enter 10-digit mobile number" 
                  maxLength="10"
                />
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
                {formData.phone && formData.phone.length > 0 && (
                  <p className={`text-xs mt-1 ${formData.phone.length === 10 ? 'text-green-500' : 'text-orange-500'}`}>
                    {formData.phone.length}/10 digits
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Position <span className="text-red-500">*</span></label>
                <select 
                  name="position" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  value={formData.position} 
                  onChange={handleChange}
                >
                  <option value="">Select Position</option>
                  {roles.map(role => <option key={role.value} value={role.label}>{role.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role Type <span className="text-red-500">*</span></label>
                <select 
                  name="role" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  value={formData.role} 
                  onChange={handleChange}
                >
                  <option value="intern">Intern</option>
                  <option value="employee">Full-Time Employee</option>
                  <option value="manager">Manager</option>
                  <option value="hr">HR Executive</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Department <span className="text-red-500">*</span></label>
                <select 
                  name="department" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  value={formData.department} 
                  onChange={handleChange}
                >
                  <option value="">Select Department</option>
                  {departments.map(dept => <option key={dept} value={dept}>{dept}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reporting To <span className="text-red-500">*</span></label>
                <select 
                  name="reportingTo" 
                  required 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  value={formData.reportingTo} 
                  onChange={handleChange}
                >
                  <option value="">Select Manager</option>
                  {reportingManagers.map(manager => <option key={manager} value={manager}>{manager}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Joining Date <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(2025-2099 only)</span>
                </label>
                <input 
                  type="date" 
                  name="joiningDate" 
                  required 
                  min="2025-01-01"
                  max="2099-12-31"
                  className={`w-full px-3 py-2 border ${errors.joiningDate ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.joiningDate} 
                  onChange={handleJoiningDateChange} 
                />
                {errors.joiningDate && (
                  <p className="text-red-500 text-xs mt-1">{errors.joiningDate}</p>
                )}
              </div>
            </div>
          </div>

          {/* Compensation Details */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <h3 className="text-md font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Compensation Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual CTC (₹) <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Must be greater than 0)</span>
                </label>
                <input 
                  type="number" 
                  name="ctc" 
                  required 
                  min="1"
                  step="1"
                  className={`w-full px-3 py-2 border ${errors.ctc ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.ctc} 
                  onChange={handleChange} 
                  placeholder="e.g., 600000" 
                />
                {errors.ctc && (
                  <p className="text-red-500 text-xs mt-1">{errors.ctc}</p>
                )}
                {formData.ctc && parseFloat(formData.ctc) > 0 && (
                  <p className="text-green-500 text-xs mt-1">✓ Valid amount</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Work Mode</label>
                <select 
                  name="workMode" 
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                  value={formData.workMode} 
                  onChange={handleChange}
                >
                  <option value="On-site">On-site (Full Time at Office)</option>
                  <option value="Hybrid">Hybrid (3 days office, 2 days WFH)</option>
                  <option value="Remote">Remote (Work From Home)</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Probation Period (months) <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Must be greater than 0)</span>
                </label>
                <input 
                  type="number" 
                  name="probationPeriod" 
                  required 
                  min="1"
                  step="1"
                  className={`w-full px-3 py-2 border ${errors.probationPeriod ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.probationPeriod} 
                  onChange={handleChange} 
                />
                {errors.probationPeriod && (
                  <p className="text-red-500 text-xs mt-1">{errors.probationPeriod}</p>
                )}
                {formData.probationPeriod && parseFloat(formData.probationPeriod) > 0 && (
                  <p className="text-green-500 text-xs mt-1">✓ Valid period</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notice Period (days) <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Must be greater than 0)</span>
                </label>
                <input 
                  type="number" 
                  name="noticePeriod" 
                  required 
                  min="1"
                  step="1"
                  className={`w-full px-3 py-2 border ${errors.noticePeriod ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.noticePeriod} 
                  onChange={handleChange} 
                />
                {errors.noticePeriod && (
                  <p className="text-red-500 text-xs mt-1">{errors.noticePeriod}</p>
                )}
                {formData.noticePeriod && parseFloat(formData.noticePeriod) > 0 && (
                  <p className="text-green-500 text-xs mt-1">✓ Valid period</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Acceptance Deadline (days) <span className="text-red-500">*</span>
                  <span className="text-xs text-gray-500 ml-2">(Must be greater than 0)</span>
                </label>
                <input 
                  type="number" 
                  name="acceptanceDays" 
                  required 
                  min="1"
                  step="1"
                  className={`w-full px-3 py-2 border ${errors.acceptanceDays ? 'border-red-500' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`} 
                  value={formData.acceptanceDays} 
                  onChange={handleChange} 
                />
                {errors.acceptanceDays && (
                  <p className="text-red-500 text-xs mt-1">{errors.acceptanceDays}</p>
                )}
                {formData.acceptanceDays && parseFloat(formData.acceptanceDays) > 0 && (
                  <p className="text-green-500 text-xs mt-1">✓ Valid deadline</p>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 justify-end pt-4">
            <button 
              type="button" 
              onClick={() => window.history.back()} 
              className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition font-medium"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-6 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition font-medium shadow-sm"
            >
              Generate Offer Letter
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OfferLetterGenerator;